// AlfaDrive flexible pricing engine.
//
// PRICING MODEL
// -------------
// Every vehicle has an "Overall Monthly Governing Price" (governing_monthly_price)
// that is used ONLY to derive the automatic standard daily rate:
//
//   Standard Daily = governing_monthly_price / days_in_that_calendar_month
//
// The vehicle also has an "Actual Monthly Rental Price" (price_monthly) — the
// special deal admin sets manually. This monthly deal is ONLY applied when the
// rental duration is exactly 29 or 30 days, or as the first-30-day block of a
// longer rental.
//
// Weekly rate is manually set per vehicle (price_weekly).
//
// PER-VEHICLE DISCOUNT TIERS
// --------------------------
// Each vehicle can define week 1/2/3/4 discount percentages (applied to that
// week's weekly rate) and an extra-days discount percent (applied to any
// day-rate rentals past the last full week).
//
// TIER LOGIC (< 29 days)
// ----------------------
//   fullWeeks = floor(days / 7)
//   remainder = days - fullWeeks * 7
//   for each week i in [1..fullWeeks]:
//     weekly_rate × (1 − week_i_discount_pct / 100)
//   for each remainder day:
//     (governing / days_in_that_calendar_month) × (1 − extra_days_discount_pct / 100)
//
// 29 or 30 DAYS -> single line at price_monthly ("Monthly rental")
//
// > 30 DAYS -> price_monthly (for first 30 days) + tier(days − 30) using
//              extra-days daily rate (calendar-month-aware)
//
// MEMBER PRICING
// --------------
// - Fixed mode (default): uses member_price_weekly, member_price_monthly,
//   member_price_daily as the base rates. Per-week discounts still apply.
// - percent_off_standard mode: everything = standard × (1 − member_pct/100).
//
// Hourly is member-only (uses price_hourly on the vehicle).
//
// NO CAP: The engine never charges LESS than the sum of the days rented.
// Every rental day is always charged.

const HOUR_MS = 3600 * 1000;
const DAY_MS = 24 * HOUR_MS;

export const NONMEMBER_MIN_DAYS = 7; // fallback if vehicle field missing
export const MEMBER_MIN_HOURS = 1;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function daysInMonth(year, month0) {
  return new Date(year, month0 + 1, 0).getDate();
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthLabel(date) {
  return `${MONTH_ABBR[date.getMonth()]} ${date.getFullYear()}`;
}

export function durationHours(startISO, endISO) {
  try {
    const s = new Date(startISO);
    const e = new Date(endISO);
    return Math.max(0, Math.round((e - s) / HOUR_MS));
  } catch {
    return 0;
  }
}

export function hoursToDays(hours) {
  if (!hours || hours <= 0) return 0;
  return Math.ceil(hours / 24);
}

export function unitLabel(unit, count) {
  const plural = count > 1 ? 's' : '';
  if (unit === 'hour') return `hour${plural}`;
  if (unit === 'day') return `day${plural}`;
  if (unit === 'week') return `week${plural}`;
  if (unit === 'month') return `month${plural}`;
  return unit;
}

// -----------------------------------------------------------------------------
// Rate resolution (Standard vs Member)
// -----------------------------------------------------------------------------

function resolveRates(vehicle) {
  const gov = Number(vehicle.governing_monthly_price || 0);
  const stdWeekly = Number(vehicle.price_weekly || 0);
  const stdMonthly = Number(vehicle.price_monthly || 0);
  const mode = vehicle.member_pricing_mode || 'fixed';
  const memPct = Number(vehicle.member_discount_percent || 0);

  const std = {
    governingMonthly: gov,
    weekly: stdWeekly,
    monthlyRental: stdMonthly,
    dailyMode: 'governing', // per-calendar-month daily rate
    fixedDaily: 0,
    week1: Number(vehicle.week_1_discount_pct || 0),
    week2: Number(vehicle.week_2_discount_pct || 0),
    week3: Number(vehicle.week_3_discount_pct || 0),
    week4: Number(vehicle.week_4_discount_pct || 0),
    extraDaysPct: Number(vehicle.extra_days_discount_pct || 0),
  };

  let mem;
  if (mode === 'percent_off_standard') {
    const factor = 1 - memPct / 100;
    mem = {
      governingMonthly: round2(gov * factor),
      weekly: round2(stdWeekly * factor),
      monthlyRental: round2(stdMonthly * factor),
      dailyMode: 'governing',
      fixedDaily: 0,
      week1: std.week1,
      week2: std.week2,
      week3: std.week3,
      week4: std.week4,
      extraDaysPct: std.extraDaysPct,
      memberPercentOff: memPct,
    };
  } else {
    // Fixed member fields
    const memWeekly = Number(vehicle.member_price_weekly || 0) || stdWeekly;
    const memMonthly = Number(vehicle.member_price_monthly || 0) || stdMonthly;
    const memDaily = Number(vehicle.member_price_daily || 0);
    mem = {
      governingMonthly: gov, // reserved (used only if fixedDaily==0)
      weekly: memWeekly,
      monthlyRental: memMonthly,
      dailyMode: memDaily > 0 ? 'fixed' : 'governing',
      fixedDaily: memDaily,
      week1: std.week1,
      week2: std.week2,
      week3: std.week3,
      week4: std.week4,
      extraDaysPct: std.extraDaysPct,
    };
  }
  return { std, mem };
}

// -----------------------------------------------------------------------------
// Split extra days by calendar month (governing mode) or return a single group (fixed).
// dayOffset = index of the first extra-day relative to the rental start date.
// -----------------------------------------------------------------------------

function splitExtraDays(startISO, dayOffset, count, rateConfig) {
  if (count <= 0) return [];
  if (rateConfig.dailyMode === 'fixed') {
    return [{ count, ratePerDay: rateConfig.fixedDaily, monthLabel: null }];
  }
  const start = new Date(startISO);
  const groups = [];
  let current = null;
  for (let i = 0; i < count; i++) {
    const d = new Date(start.getTime() + (dayOffset + i) * DAY_MS);
    const y = d.getFullYear();
    const m = d.getMonth();
    if (!current || current.year !== y || current.month0 !== m) {
      const dim = daysInMonth(y, m);
      const rate = round2(rateConfig.governingMonthly / dim);
      current = {
        year: y,
        month0: m,
        monthLabel: monthLabel(d),
        daysInMonth: dim,
        ratePerDay: rate,
        count: 0,
      };
      groups.push(current);
    }
    current.count += 1;
  }
  return groups;
}

// -----------------------------------------------------------------------------
// Core multi-day compute
// -----------------------------------------------------------------------------

function computeMultiDay(days, rateConfig, startISO) {
  const rows = [];
  let total = 0;

  const weeklyRateAt = (weekIdx) => {
    const pct = [rateConfig.week1, rateConfig.week2, rateConfig.week3, rateConfig.week4][weekIdx - 1] || 0;
    return { rate: round2(rateConfig.weekly * (1 - pct / 100)), pct };
  };

  // 29 or 30 days -> Monthly rental deal (single line)
  if (days === 29 || days === 30) {
    const m = rateConfig.monthlyRental;
    rows.push({
      unit: 'month',
      count: 1,
      rate: m,
      subtotal: m,
      discount_pct: 0,
      note: `Monthly rental (${days} days)`,
    });
    total = m;
    return { total: round2(total), breakdown: rows };
  }

  // > 30 days -> monthly (first 30) + tier remainder as extra days
  if (days > 30) {
    const m = rateConfig.monthlyRental;
    rows.push({
      unit: 'month',
      count: 1,
      rate: m,
      subtotal: m,
      discount_pct: 0,
      note: 'Monthly rental (first 30 days)',
    });
    total += m;

    const extra = days - 30;
    const groups = splitExtraDays(startISO, 30, extra, rateConfig);
    for (const g of groups) {
      const rate = round2(g.ratePerDay * (1 - (rateConfig.extraDaysPct || 0) / 100));
      const subtotal = round2(g.count * rate);
      rows.push({
        unit: 'day',
        count: g.count,
        rate,
        subtotal,
        discount_pct: rateConfig.extraDaysPct || 0,
        note: g.monthLabel
          ? `${g.count} extra day${g.count > 1 ? 's' : ''} in ${g.monthLabel}`
          : `${g.count} extra day${g.count > 1 ? 's' : ''}`,
      });
      total += subtotal;
    }
    return { total: round2(total), breakdown: rows };
  }

  // < 29 days: full tier logic (weeks + extra days)
  const fullWeeks = Math.floor(days / 7);
  const remainderDays = days - fullWeeks * 7;

  for (let i = 1; i <= fullWeeks; i++) {
    const { rate, pct } = weeklyRateAt(i);
    rows.push({
      unit: 'week',
      count: 1,
      rate,
      subtotal: rate,
      discount_pct: pct,
      note: `Week ${i}${pct > 0 ? ` −${pct}%` : ''}`,
    });
    total += rate;
  }

  if (remainderDays > 0) {
    const groups = splitExtraDays(startISO, fullWeeks * 7, remainderDays, rateConfig);
    const isFixed = rateConfig.dailyMode === 'fixed';
    for (const g of groups) {
      const pct = rateConfig.extraDaysPct || 0;
      const rate = round2(g.ratePerDay * (1 - pct / 100));
      const subtotal = round2(g.count * rate);
      const dayWord = g.count > 1 ? 'days' : 'day';
      let note;
      if (isFixed) {
        note = `${g.count} extra ${dayWord}${pct > 0 ? ` −${pct}%` : ''}`;
      } else if (groups.length === 1) {
        note = `${g.count} extra ${dayWord}${pct > 0 ? ` −${pct}%` : ''}`;
      } else {
        note = `${g.count} ${dayWord} in ${g.monthLabel}${pct > 0 ? ` −${pct}%` : ''}`;
      }
      rows.push({
        unit: 'day',
        count: g.count,
        rate,
        subtotal,
        discount_pct: pct,
        note,
      });
      total += subtotal;
    }
  }

  return { total: round2(total), breakdown: rows };
}

// -----------------------------------------------------------------------------
// Hourly rate (member-only)
// -----------------------------------------------------------------------------

export function hourlyRate(vehicle, isMember) {
  if (!isMember) return null;
  const set = Number(vehicle.price_hourly || 0);
  if (set > 0) return set;
  // Fallback: member daily / 5 hours breakeven
  const memDaily = Number(vehicle.member_price_daily || 0);
  if (memDaily > 0) return round2(memDaily / 5);
  const gov = Number(vehicle.governing_monthly_price || 0);
  if (gov > 0) return round2(gov / 30 / 5 * 0.85);
  return null;
}

// -----------------------------------------------------------------------------
// Master compute
// -----------------------------------------------------------------------------

export function computePricing({ vehicle, startISO, endISO, isMember, forceHourly }) {
  if (!vehicle) return { ok: false, error: 'Missing vehicle', hours: 0, days: 0 };
  const hours = durationHours(startISO, endISO);
  if (hours <= 0) return { ok: false, error: 'Return time must be after pick-up', hours: 0, days: 0 };
  const days = hoursToDays(hours);

  const minNon = Number(vehicle.min_days_non_member) || NONMEMBER_MIN_DAYS;
  const minMem = Number(vehicle.min_days_member) || 1;

  // Hourly branch (member only, < 24h or forced)
  const canHourly = isMember && (forceHourly || hours < 24);
  if (canHourly) {
    const memHr = hourlyRate(vehicle, true);
    if (!memHr || memHr <= 0) {
      return { ok: false, error: 'Hourly rate not configured for this vehicle.', hours, days, memberOnly: true };
    }
    const memTotal = round2(hours * memHr);
    return {
      ok: true, hours, days, mode: 'hourly', memberOnly: true,
      standard: null,
      member: {
        total: memTotal,
        breakdown: [{
          unit: 'hour',
          count: hours,
          rate: memHr,
          subtotal: memTotal,
          discount_pct: 0,
          note: `${hours} hour${hours > 1 ? 's' : ''}`,
        }],
      },
      used: 'member',
      totalUsed: memTotal,
      savings: 0,
    };
  }

  // Multi-day path
  const minDays = isMember ? minMem : minNon;
  if (days < minDays) {
    return {
      ok: false,
      error: isMember
        ? `Please pick at least ${minDays} day${minDays > 1 ? 's' : ''}.`
        : `Non-members must book at least ${minDays} days. Join Drive Alfa Club to unlock hourly and short-term rentals.`,
      hours, days, minDays,
      memberOnly: !isMember,
    };
  }

  const rates = resolveRates(vehicle);
  const std = computeMultiDay(days, rates.std, startISO);
  const mem = computeMultiDay(days, rates.mem, startISO);

  const used = isMember ? 'member' : 'standard';
  const activeTotal = used === 'member' ? mem.total : std.total;
  const savings = round2(Math.max(0, std.total - mem.total));

  return {
    ok: true,
    hours,
    days,
    mode: 'multi_day',
    standard: std,
    member: mem,
    used,
    totalUsed: activeTotal,
    savings,
  };
}

export function inferPlanType(breakdown) {
  const units = new Set((breakdown || []).map((b) => b.unit));
  if (units.has('month')) return 'monthly';
  if (units.has('week')) return 'weekly';
  if (units.has('day')) return 'daily';
  if (units.has('hour')) return 'hourly';
  return 'daily';
}
